/**
 * Invitar a crédito — campaña por WhatsApp.
 *
 * Lista los asociados activos que aún NO tienen crédito emergente. Para cada
 * uno, un botón que abre WhatsApp con un mensaje ya redactado (wa.me), para
 * que Nataly los invite con un tap desde el celular. El mensaje es editable
 * ({nombre} se reemplaza por el primer nombre). Marca "contactado" localmente.
 */
import { useEffect, useMemo, useState } from 'react'
import { MagnifyingGlass, WhatsappLogo, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Row = { id: string; nombre_completo: string; neo_id: string | null; no_asociado: string; telefono: string; tiene_ahorro: boolean }

const MSG_DEFAULT =
  '¡Hola {nombre}! 👋 Te escribo de Neocoop. Ya tenés disponible tu *Crédito Emergente*: ' +
  'lo pedís en minutos desde la app, sin filas y sin fiador. Y si querés hacer crecer tu ' +
  'dinero, también podés invertir a plazo fijo con nosotros y ganar intereses. ' +
  '¿Querés que te cuente cómo? Estamos para ayudarte. 🧡'

const LS_KEY = 'neocoop_invitar_credito_contactados'
const primerNombre = (n: string) => (n ?? '').trim().split(/\s+/)[0] || ''
const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s

export default function NeoCoreInvitarCreditoPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [msg, setMsg] = useState(MSG_DEFAULT)
  const [contactados, setContactados] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') } catch { return {} }
  })

  const load = async () => {
    setLoading(true)
    const { data } = await supabase.rpc('asociados_sin_credito_emergente')
    setRows((data ?? []) as Row[])
    setLoading(false)
  }
  useEffect(() => { void load() }, [])
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(contactados)) }, [contactados])

  const marcar = (id: string) => setContactados((c) => ({ ...c, [id]: !c[id] }))

  const waHref = (r: Row) => {
    const num = (r.telefono || '').replace(/\D/g, '')
    const texto = msg.replace(/\{nombre\}/g, cap(primerNombre(r.nombre_completo)))
    return `https://wa.me/${num}?text=${encodeURIComponent(texto)}`
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return rows
    return rows.filter((r) =>
      r.nombre_completo.toLowerCase().includes(s) ||
      (r.neo_id ?? '').toLowerCase().includes(s) ||
      r.no_asociado.toLowerCase().includes(s))
  }, [rows, q])

  const pendientes = rows.filter((r) => !contactados[r.id]).length

  return (
    <div style={{ padding: '16px 16px 60px', maxWidth: 620, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>Invitar a crédito</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Asociados sin crédito emergente · <strong>{pendientes}</strong> por invitar de {rows.length}
          </p>
        </div>
        <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <ArrowsClockwise size={13} weight="bold" />
        </button>
      </div>

      {/* Mensaje editable */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
        <label style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>
          Mensaje (se envía a cada uno)
        </label>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={5}
          style={{ width: '100%', marginTop: 8, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--hairline)', fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', color: 'var(--ink)' }}
        />
        <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>
          <code>{'{nombre}'}</code> se reemplaza por el nombre del asociado. Editalo si querés.
        </p>
      </div>

      {/* Búsqueda */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', borderRadius: 10, padding: '10px 12px', border: '1px solid var(--hairline)', marginBottom: 12 }}>
        <MagnifyingGlass size={15} color="var(--muted-foreground)" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar asociado…"
          style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 14, fontFamily: 'inherit', color: 'var(--ink)' }} />
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin asociados para invitar.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((r) => {
            const done = !!contactados[r.id]
            return (
              <div key={r.id} style={{
                background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14,
                padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                opacity: done ? 0.55 : 1,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.nombre_completo}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
                    {r.neo_id ?? r.no_asociado} · {r.telefono}
                    {r.tiene_ahorro ? ' · 💰 ahorro' : ''}
                  </div>
                </div>
                <button
                  onClick={() => marcar(r.id)}
                  title={done ? 'Marcar como no contactado' : 'Marcar como contactado'}
                  style={{ all: 'unset', cursor: 'pointer', color: done ? 'var(--ok-ink, #0f8a57)' : 'var(--muted-foreground)' }}
                >
                  <CheckCircle size={26} weight={done ? 'fill' : 'regular'} />
                </button>
                <a
                  href={waHref(r)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => { if (!done) marcar(r.id) }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
                    background: '#25D366', color: '#fff', padding: '10px 16px', borderRadius: 10,
                    fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
                  }}
                >
                  <WhatsappLogo size={18} weight="fill" /> Enviar
                </a>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
